// src/hooks/useTriageForm.js
//
// State machine for the triage form flow.
// Manages answers, step progression, conditional logic,
// move-date derivations, and submission.
//
// Mirrors ProfilePage.js conventions exactly:
//   - move_date stored as YYYY-MM-DD ISO string
//   - move_day only collected when move_month === '7' (July)
//   - residency_year_type and spain_residency_status derived, not asked
//
// Step IDs (in order):
//   move_date → tax_year → pre_move_fork → comunidad_autonoma →
//   citizenship_status → marital_status → income_types →
//   property_ownership → major_events → assets_and_net_worth →
//   filed_spain_before → beckham_status
//
// pre_move_fork is a synthetic step — only shown when move_date
// is in the future relative to the selected tax year.
//
// assets_and_net_worth is a combined step — foreign_account_band and
// worldwide_net_worth_band are asked on the same screen (net worth first,
// then foreign accounts). Both are independently required; they aren't a
// parent/child conditional pair like the other inline sub-questions —
// each drives its own separate flags (Modelo 720 vs. wealth tax) and
// neither can be skipped based on the other's answer.
//
// Tax year options are prior/current/next year (not an open-ended range),
// with the prior year dropping off once its June 30 filing deadline passes.
// See buildTaxYearOptions below.

import { useState, useCallback, useMemo } from 'react'
import { slots, getSlotOptions, toggleMultiWithExclusive } from '../lib/slotConfig'
import { getActivePartnerRef } from '../lib/partnerAttribution'
import { getFirstReferrer, getDeviceType } from '../lib/clientMeta'
import posthog from '../lib/posthog'

// ─── Constants ────────────────────────────────────────────────────────────────

const CURRENT_YEAR = new Date().getFullYear()

// Steps in declared order — controls progress bar and back navigation
const STEP_ORDER = [
  'move_date',
  'tax_year',
  'pre_move_fork',          // conditional: future movers only
  'comunidad_autonoma',
  'citizenship_status',     // eu_citizen shown inline when eu_citizen option selected
  'marital_status',
  'asset_split',            // conditional: married/civil_partnership only — shown inline in marital_status step
  'income_types',           // rental_location, pension_source, self_employment_country, business_partnership_type shown inline
  'property_ownership',
  'major_events',           // property_sale_location, retirement_withdrawal_type shown inline
  'assets_and_net_worth',   // combined: worldwide_net_worth_band + foreign_account_band
  'filed_spain_before',
  'beckham_status',         // conditional: recent/future mover + employment
]

// ─── Initial state ─────────────────────────────────────────────────────────────

const INITIAL_ANSWERS = {
  // Move date components (mirrors ProfilePage.js)
  move_month: '',
  move_year:  '',
  move_day:   '',
  move_date:  null,        // ISO string — derived on submit

  // Tax year
  tax_year: null,

  // Pre-move fork (synthetic step)
  pre_move_choice: null,   // 'continue_planning' | 'come_back_later'

  // Spain location
  comunidad_autonoma: '',

  // Citizenship
  citizenship_status: '',  // us_citizen | green_card | eu_citizen | other
  eu_citizen: false,       // true if eu_citizen selected (also catches dual US/EU)

  // Marital
  marital_status: '',      // married | civil_partnership | single | separated_divorced | widowed
  asset_split: '',         // conditional — shown when married/civil_partnership selected

  // Income
  income_types: [],
  self_employment_country: '',          // conditional on self_employment
  business_partnership_type: '',        // conditional on business_partnership
  rental_location: '',     // spain | us | both (conditional)
  pension_source: '',      // spanish | us | both (conditional)
  pension_is_government: null, // conditional on pension_source !== 'spanish'

  // Accounts & assets
  foreign_account_band: '',
  property_ownership: [],
  major_events: [],
  retirement_withdrawal_type: '', // conditional on 'retirement_withdrawal' event
  property_sale_location: '', // spain | us | other_foreign (conditional)
  worldwide_net_worth_band: '',

  // Filing history
  filed_spain_before: null, // true | false

  // Beckham (conditional)
  beckham_status: '',
}

// ─── Derivation helpers (mirrors ProfilePage.js exactly) ──────────────────────

function buildMoveDateISO(move_month, move_year, move_day) {
  if (!move_month || !move_year) return null
  const month = String(move_month).padStart(2, '0')
  const day   = String(move_month === '7' ? (move_day || 1) : 1).padStart(2, '0')
  return `${move_year}-${month}-${day}`
}

function deriveResidencyYearType(moveDateISO, taxYear) {
  if (!moveDateISO || !taxYear) return null
  const moveYear = new Date(moveDateISO).getFullYear()
  if (moveYear === taxYear)  return 'split_year'
  if (moveYear < taxYear)   return 'full_year_spain'
  return null  // move is in the future relative to this tax year
}

function deriveSpainResidencyStatus(moveDateISO, residencyYearType, taxYear) {
  if (!moveDateISO || !residencyYearType || !taxYear) return null
  if (residencyYearType === 'full_year_spain') return 'resident_full_year'
  if (residencyYearType === 'split_year') {
    const arrival    = new Date(moveDateISO)
    const yearEnd    = new Date(`${taxYear}-12-31`)
    const daysInSpain = Math.floor((yearEnd - arrival) / 86400000) + 1
    return daysInSpain >= 183 ? 'resident_partial_year' : 'non_resident'
  }
  if (residencyYearType === 'full_year_us') return 'non_resident'
  return null
}

// ─── Tax year options ──────────────────────────────────────────────────────────
// Window is prior year + current year + next year — not an open-ended range
// back to some fixed MIN_TAX_YEAR. Prior year only appears while its filing
// deadline (Modelo 100, June 30 of the current year) hasn't passed yet;
// once it has, that year drops off the list. Then filtered so we never show
// a year entirely before the person moved to Spain.

function buildTaxYearOptions(moveDateISO) {
  const today          = new Date()
  const priorYear       = CURRENT_YEAR - 1
  const filingDeadline  = new Date(`${CURRENT_YEAR}-06-30`)
  const priorYearIsOpen = today <= filingDeadline

  const baseYears = [
    ...(priorYearIsOpen ? [priorYear] : []),
    CURRENT_YEAR,
    CURRENT_YEAR + 1,
  ]

  if (!moveDateISO) return baseYears

  const moveYear = new Date(moveDateISO).getFullYear()

  // Future movers beyond the base window: filtering baseYears by
  // y >= moveYear would wipe out every candidate (e.g. moveYear 2028
  // against a 2026/2027 window). Anchor the window to the move year
  // itself instead, so a far-future move still gets sensible options.
  if (moveYear > CURRENT_YEAR + 1) {
    return [moveYear, moveYear + 1]
  }

  return baseYears.filter(y => y >= moveYear)
}

// ─── Beckham conditional ───────────────────────────────────────────────────────
// Show Beckham question if:
//   - Recent mover (moved within last 2 years) OR planned mover (move in future)
//   - AND income includes employment or self_employment

function shouldShowBeckham(answers) {
  const { move_month, move_year, tax_year, income_types, pre_move_choice } = answers

  // If they chose "come back later" at the fork, skip everything
  if (pre_move_choice === 'come_back_later') return false

  if (!move_year || !tax_year) return false

  const moveYear = parseInt(move_year, 10)
  const hasEmploymentIncome =
    income_types.includes('employment_us') ||
    income_types.includes('employment_es') ||
    income_types.includes('self_employment')

  if (!hasEmploymentIncome) return false

  // Recent mover: moved within last 2 years of tax year
  const isRecentMover = moveYear >= tax_year - 2

  // Planned/future mover: move year is after the tax year being assessed
  const isFutureMover = moveYear > tax_year

  return isRecentMover || isFutureMover
}

// ─── Pre-move fork ─────────────────────────────────────────────────────────────
// Show fork if move_date is in the future relative to the selected tax year

function shouldShowPreMoveFork(answers) {
  const { move_month, move_year, tax_year } = answers
  if (!move_month || !move_year || !tax_year) return false
  const moveYear = parseInt(move_year, 10)
  return moveYear > tax_year
}

// ─── Step visibility ───────────────────────────────────────────────────────────

function isStepVisible(stepId, answers) {
  switch (stepId) {
    case 'pre_move_fork':
      return shouldShowPreMoveFork(answers)
    case 'eu_citizen':
      return false  // shown inline under citizenship_status eu_citizen option
    case 'asset_split':
      // Shown inline in marital_status step — keep as step too for back-nav edge cases
      return false
    case 'beckham_status':
      return shouldShowBeckham(answers)
    default:
      return true
  }
}

// Ordered list of visible steps given current answers
function getVisibleSteps(answers) {
  return STEP_ORDER.filter(stepId => isStepVisible(stepId, answers))
}

// ─── Step validation ───────────────────────────────────────────────────────────
// Returns true if the current step has a valid answer to proceed

function isStepComplete(stepId, answers) {
  switch (stepId) {
    case 'move_date':
      return !!(answers.move_month && answers.move_year)
    case 'tax_year':
      return !!answers.tax_year
    case 'pre_move_fork':
      return !!answers.pre_move_choice
    case 'comunidad_autonoma':
      return !!answers.comunidad_autonoma
    case 'citizenship_status':
      return !!answers.citizenship_status
    case 'eu_citizen':
      return true  // informational confirmation, always complete
    case 'marital_status': {
      if (!answers.marital_status) return false
      const requiresAssetSplit = ['married', 'civil_partnership'].includes(answers.marital_status)
      return requiresAssetSplit ? !!answers.asset_split : true
    }
    case 'asset_split':
      return !!answers.asset_split
    case 'income_types':
      return answers.income_types.length > 0
    case 'rental_location':
      return !!answers.rental_location
    case 'pension_source':
      return !!answers.pension_source
    case 'property_ownership':
      return answers.property_ownership.length > 0
    case 'major_events':
      return answers.major_events.length > 0
    case 'property_sale_location':
      return !!answers.property_sale_location
    case 'assets_and_net_worth':
      return !!answers.worldwide_net_worth_band && !!answers.foreign_account_band
    case 'filed_spain_before':
      return answers.filed_spain_before !== null
    case 'beckham_status':
      return !!answers.beckham_status
    default:
      return false
  }
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useTriageForm() {
  const [answers, setAnswers]         = useState(INITIAL_ANSWERS)
  const [currentStep, setCurrentStep] = useState('move_date')
  const [submitting, setSubmitting]   = useState(false)
  const [submitError, setSubmitError] = useState(null)
  const [result, setResult]           = useState(null)  // set after successful submit

  // ── Derived values ──────────────────────────────────────────────────────────

  const moveDateISO = useMemo(
    () => buildMoveDateISO(answers.move_month, answers.move_year, answers.move_day),
    [answers.move_month, answers.move_year, answers.move_day]
  )

  const taxYearOptions = useMemo(
    () => buildTaxYearOptions(moveDateISO),
    [moveDateISO]
  )

  const residencyYearType = useMemo(
    () => deriveResidencyYearType(moveDateISO, answers.tax_year),
    [moveDateISO, answers.tax_year]
  )

  const spainResidencyStatus = useMemo(
    () => deriveSpainResidencyStatus(moveDateISO, residencyYearType, answers.tax_year),
    [moveDateISO, residencyYearType, answers.tax_year]
  )

  const visibleSteps = useMemo(
    () => getVisibleSteps(answers),
    [answers]
  )

  const currentStepIndex = visibleSteps.indexOf(currentStep)
  const totalSteps        = visibleSteps.length
  const progressPct       = totalSteps > 0
    ? Math.round(((currentStepIndex + 1) / totalSteps) * 100)
    : 0

  const canProceed = isStepComplete(currentStep, answers)
  const isLastStep = currentStepIndex === visibleSteps.length - 1

  // ── Answer setters ──────────────────────────────────────────────────────────

  const set = useCallback((field, value) => {
    setAnswers(prev => ({ ...prev, [field]: value }))
  }, [])

  // Multi-select toggle — uses shared toggleMultiWithExclusive from slotConfig
  const toggleMulti = useCallback((field, value, exclusiveOptions = ['none']) => {
    setAnswers(prev => {
      const next = toggleMultiWithExclusive(prev[field] || [], value, exclusiveOptions)
      return { ...prev, [field]: next }
    })
  }, [])

  // ── Navigation ──────────────────────────────────────────────────────────────

  const goNext = useCallback(() => {
    if (!canProceed) return

    posthog.capture('triage_step_completed', { step: currentStep })

    // If user chose "come back later" at fork, stop the flow
    if (currentStep === 'pre_move_fork' && answers.pre_move_choice === 'come_back_later') {
      // Signal to parent to show the "come back later" screen
      setResult({ outcome: 'deferred' })
      return
    }

    const nextIndex = currentStepIndex + 1
    if (nextIndex < visibleSteps.length) {
      setCurrentStep(visibleSteps[nextIndex])
    }
  }, [canProceed, currentStep, currentStepIndex, visibleSteps, answers.pre_move_choice])

  const goBack = useCallback(() => {
    const prevIndex = currentStepIndex - 1
    if (prevIndex >= 0) {
      setCurrentStep(visibleSteps[prevIndex])
    }
  }, [currentStepIndex, visibleSteps])

  const goToStep = useCallback((stepId) => {
    if (visibleSteps.includes(stepId)) {
      setCurrentStep(stepId)
    }
  }, [visibleSteps])

  // ── Submit ──────────────────────────────────────────────────────────────────

  const submit = useCallback(async ({ source, variant } = {}) => {
    if (!isLastStep || !canProceed) return
    setSubmitting(true)
    setSubmitError(null)

    try {
      // Partner ref now read from the persistent cookie (see
      // src/lib/partnerAttribution.js) rather than the URL directly — the
      // cookie survives however many steps/pages the user visits between
      // arriving via ?ref= and reaching this final submit, which a raw
      // location.search read at submit time did not.
      const partnerRef = getActivePartnerRef()

      const triage_inputs = {
        // Move date
        move_date: moveDateISO,

        // Tax year
        tax_year: answers.tax_year,

        // Derived residency
        residency_year_type:    residencyYearType,
        spain_residency_status: spainResidencyStatus,

        // Pre-move flag
        is_pre_move: shouldShowPreMoveFork(answers),

        // Location
        comunidad_autonoma: answers.comunidad_autonoma || null,

        // Citizenship
        citizenship_status: answers.citizenship_status,
        eu_citizen:         answers.eu_citizen || false,

        // Marital
        marital_status: answers.marital_status || null,
        asset_split:    answers.asset_split    || null,

        // Income
        income_types:                    answers.income_types,
        self_employment_country:          answers.self_employment_country          || null,
        business_partnership_type:        answers.business_partnership_type        || null,
        rental_location:                 answers.rental_location  || null,
        pension_source:                  answers.pension_source   || null,
        pension_is_government:           answers.pension_is_government,

        // Accounts & assets
        foreign_account_band:      answers.foreign_account_band,
        property_ownership:        answers.property_ownership,
        major_events:              answers.major_events,
        retirement_withdrawal_type: answers.retirement_withdrawal_type || null,
        property_sale_location:    answers.property_sale_location   || null,
        worldwide_net_worth_band:  answers.worldwide_net_worth_band,

        // Filing history
        filed_spain_before: answers.filed_spain_before,

        // Optional
        beckham_status: answers.beckham_status || null,

        // Partner referral
        partner_ref: partnerRef,
      }

      const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/triage-submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}` },
        body: JSON.stringify({
          triage_inputs,
          triage_source:  source  || null,
          triage_variant: variant || null,
          referrer: getFirstReferrer(),
          device_type: getDeviceType(),
        }),
      })

      if (!response.ok) {
        const err = await response.json().catch(() => ({}))
        throw new Error(err.error || `Server error ${response.status}`)
      }

      const data = await response.json()
      posthog.capture('triage_completed', {
        tax_year: answers.tax_year,
        source: source || 'unknown',
        has_partner_referral: Boolean(partnerRef),
      })
      setResult({ outcome: 'complete', ...data })

    } catch (err) {
      setSubmitError(err.message || 'Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }, [
    isLastStep, canProceed, answers, moveDateISO,
    residencyYearType, spainResidencyStatus,
  ])

  // ── Exposed API ─────────────────────────────────────────────────────────────

  return {
    // State
    answers,
    currentStep,
    visibleSteps,
    currentStepIndex,
    totalSteps,
    progressPct,
    canProceed,
    isLastStep,
    submitting,
    submitError,
    result,

    // Derived
    moveDateISO,
    taxYearOptions,
    residencyYearType,
    spainResidencyStatus,

    // Setters
    set,
    toggleMulti,

    // Navigation
    goNext,
    goBack,
    goToStep,

    // Actions
    submit,
  }
}

// ─── Step metadata (for UI rendering) ────────────────────────────────────────
// Each step knows its question text, field(s), and answer options.
// Imported by TriagePage.js to render the correct UI per step.

export const STEP_CONFIG = {

  move_date: {
    id: 'move_date',
    question: 'When did you move to Spain?',
    hint: null,
    type: 'move_date',
  },

  tax_year: {
    id: 'tax_year',
    question: 'Which tax year are you interested in?',
    hint: "We'll focus your snapshot on this year's obligations.",
    type: 'tax_year',
  },

  pre_move_fork: {
    id: 'pre_move_fork',
    question: "It looks like you haven't moved yet.",
    hint: "Our tool is optimised for people already living in Spain. You can continue for early planning, or come back once you've arrived.",
    type: 'single_select',
    field: 'pre_move_choice',
    options: [
      { value: 'continue_planning', label: "Continue anyway — I'm planning ahead" },
      { value: 'come_back_later',   label: "Come back once I've moved" },
    ],
  },

  comunidad_autonoma: {
    id: 'comunidad_autonoma',
    question: 'Which region of Spain do you live in?',
    hint: 'Regional rules affect wealth tax, inheritance tax, and certain deductions.',
    type: 'single_select',
    field: 'comunidad_autonoma',
    // Matches the calculator's own control for this exact field — 19 options
    // is too many for a button grid.
    renderAs: 'dropdown',
    dropdownPlaceholder: 'Select a comunidad…',
    options: getSlotOptions('comunidad_autonoma'),
  },

  citizenship_status: {
    id: 'citizenship_status',
    question: 'What is your citizenship status?',
    hint: 'This determines whether additional US or EU filing obligations apply.',
    type: 'single_select',
    field: 'citizenship_status',
    options: getSlotOptions('citizenship_status'),
  },

  eu_citizen: {
    id: 'eu_citizen',
    question: 'Do you also hold US citizenship or a Green Card?',
    hint: 'Some people hold both EU and US citizenship. This affects your US filing obligations.',
    type: 'single_select',
    field: 'eu_citizen',
    options: [
      { value: true,  label: 'Yes — I also have US citizenship or a Green Card' },
      { value: false, label: 'No — EU citizenship only' },
    ],
  },

  marital_status: {
    id: 'marital_status',
    question: 'What is your marital status?',
    hint: 'Marital status affects wealth tax calculations and certain deductions.',
    type: 'single_select',
    field: 'marital_status',
    // Fixed: was a local 4-value list (civil_partnership unreachable,
    // 'divorced' didn't match canonical 'separated_divorced'). Now reads
    // from the single canonical definition in slotConfig.js so this can
    // never drift from it again.
    options: getSlotOptions('marital_status'),
  },

  asset_split: {
    id: 'asset_split',
    question: 'How are your assets broadly split between you and your partner?',
    hint: 'Wealth tax is assessed individually in Spain. The split affects whether thresholds apply to you.',
    type: 'single_select',
    field: 'asset_split',
    // Was a hardcoded options list (joint/mostly_mine/mostly_partner) that
    // had drifted from slotConfig.js's canonical values
    // (roughly_equal/one_spouse_more/all_one_spouse) — same field, two
    // different vocabularies, harmless only because nothing read
    // facts.assetSplit yet. Now sourced from the single canonical list so
    // this can't drift again.
    options: getSlotOptions('asset_split'),
  },

  income_types: {
    id: 'income_types',
    question: 'What types of income did you have that year?',
    hint: 'Select all that apply.',
    type: 'multi_select',
    field: 'income_types',
    noneValue: 'none',
    options: getSlotOptions('income_types'),
  },

  rental_location: {
    id: 'rental_location',
    question: 'Where is the rental property located?',
    hint: 'This determines which country\'s rules apply to the rental income.',
    type: 'single_select',
    field: 'rental_location',
    options: [
      { value: 'spain', label: 'Spain only' },
      { value: 'us',    label: 'US only' },
      { value: 'both',  label: 'Both Spain and US' },
      { value: 'other', label: 'Another country' },
    ],
  },

  pension_source: {
    id: 'pension_source',
    question: 'Where is your pension or retirement income from?',
    hint: 'US and Spanish pensions have different treaty treatment in Spain.',
    type: 'single_select',
    field: 'pension_source',
    options: [
      { value: 'spanish', label: 'Spanish pension or retirement plan' },
      { value: 'us',      label: 'US pension, 401(k), IRA, or Social Security' },
      { value: 'both',    label: 'Both Spanish and US sources' },
      { value: 'other',   label: 'Another country' },
    ],
  },

  // NEW — shown inline under pension_source when the answer is 'us', 'both', or 'other'.
  pension_is_government: {
    id: 'pension_is_government',
    question: 'Is this a government or civil-service pension (federal, state, or military), or private-sector?',
    hint: 'Government pensions often have different treaty sourcing rules than private pensions.',
    type: 'single_select',
    field: 'pension_is_government',
    options: getSlotOptions('pension_is_government'),
  },

  // NEW — shown inline under income_types when 'business_partnership' is selected.
  business_partnership_type: {
    id: 'business_partnership_type',
    question: 'Is this from a US pass-through entity, or a foreign company?',
    hint: "US LLC/S-corp classification in Spain is genuinely unsettled — this helps us flag it correctly.",
    type: 'single_select',
    field: 'business_partnership_type',
    options: getSlotOptions('business_partnership_type'),
  },

  foreign_account_band: {
    id: 'foreign_account_band',
    question: 'How much did you have in financial accounts outside Spain?',
    hint: 'Include bank accounts, brokerages, retirement accounts, and any other non-Spanish accounts. Use the peak value at any point during the year.',
    type: 'single_select',
    field: 'foreign_account_band',
    options: getSlotOptions('foreign_account_band'),
  },

  property_ownership: {
    id: 'property_ownership',
    question: 'Did you own any real property during that year?',
    hint: 'Select all that apply.',
    type: 'multi_select',
    field: 'property_ownership',
    noneValue: 'none',
    options: getSlotOptions('property_ownership'),
  },

  major_events: {
    id: 'major_events',
    question: 'Did any of these happen during that tax year?',
    hint: 'Select all that apply.',
    type: 'multi_select',
    field: 'major_events',
    noneValue: 'none',
    options: getSlotOptions('major_events'),
  },

  // NEW — shown inline under major_events when 'retirement_withdrawal' is selected.
  retirement_withdrawal_type: {
    id: 'retirement_withdrawal_type',
    question: 'What type of account was this from?',
    hint: 'Roth accounts have genuinely unsettled Spanish tax treatment.',
    type: 'single_select',
    field: 'retirement_withdrawal_type',
    options: getSlotOptions('retirement_withdrawal_type'),
  },

  property_sale_location: {
    id: 'property_sale_location',
    question: 'Where was the property you sold located?',
    hint: 'This determines which country\'s capital gains rules apply.',
    type: 'single_select',
    field: 'property_sale_location',
    options: [
      { value: 'spain',         label: 'Spain' },
      { value: 'us',            label: 'United States' },
      { value: 'other_foreign', label: 'Another country' },
    ],
  },

  // Combined step — worldwide_net_worth_band and foreign_account_band asked
  // together on one screen. Both required, independent of each other.
  assets_and_net_worth: {
    id: 'assets_and_net_worth',
    question: 'A couple of questions about your finances',
    hint: null,
    type: 'assets_and_net_worth',
    netWorth: {
      label: 'Roughly what was your worldwide net worth at the end of that tax year?',
      hint:  'Include property, investments, bank accounts, retirement accounts, and business interests, minus debts. A rough range is fine.',
      field: 'worldwide_net_worth_band',
      options: getSlotOptions('worldwide_net_worth_band'),
    },
    foreignAccounts: {
      label: 'How much did you have in financial accounts outside Spain?',
      hint:  'Include bank accounts, brokerages, retirement accounts, and any other non-Spanish accounts. Use the peak value at any point during the year.',
      field: 'foreign_account_band',
      options: getSlotOptions('foreign_account_band'),
    },
  },

  worldwide_net_worth_band: {
    id: 'worldwide_net_worth_band',
    question: 'Roughly what was your worldwide net worth at the end of that tax year?',
    hint: 'Include property, investments, bank accounts, retirement accounts, and business interests, minus debts. A rough range is fine.',
    type: 'single_select',
    field: 'worldwide_net_worth_band',
    options: getSlotOptions('worldwide_net_worth_band'),
  },

  filed_spain_before: {
    id: 'filed_spain_before',
    question: 'Have you filed a Spanish tax return before?',
    hint: null,
    type: 'single_select',
    field: 'filed_spain_before',
    options: [
      { value: true,  label: 'Yes' },
      { value: false, label: 'No — this would be my first time' },
    ],
  },

  beckham_status: {
    id: 'beckham_status',
    question: slots.beckham_status.label,
    hint: slots.beckham_status.hint,
    helpText: slots.beckham_status.helpText,
    type: 'single_select',
    field: 'beckham_status',
    options: getSlotOptions('beckham_status'),
  },

}